import { DetailsResponseResponseBuilder } from 'gql/autogen/DetailsResponseResponseBuilder';
import { DetailsActivityResultResponseBuilder } from 'gql/autogen/DetailsActivityResultResponseBuilder';
import { CancellationResponseBuilder } from 'gql/autogen/CancellationResponseBuilder';
import { CancellationPolicyResponseBuilder } from 'gql/autogen/CancellationPolicyResponseBuilder';
import { PricingSummaryResponseBuilder } from 'gql/autogen/PricingSummaryResponseBuilder';
import { PricingResponseBuilder } from 'gql/autogen/PricingResponseBuilder';
import { ActivityWithContentResponseBuilder } from 'gql/autogen/ActivityWithContentResponseBuilder';
import { ContentResponseBuilder } from 'gql/autogen/ContentResponseBuilder';
import { DetailResponseBuilder } from 'gql/autogen/DetailResponseBuilder';
import { ImageResponseBuilder } from 'gql/autogen/ImageResponseBuilder';
import { ImageSizeResponseBuilder } from 'gql/autogen/ImageSizeResponseBuilder';
import { AdditionalDetailResponseBuilder } from 'gql/autogen/AdditionalDetailResponseBuilder';
import { ItineraryReferenceResponseBuilder } from 'gql/autogen/ItineraryReferenceResponseBuilder';
import { ItineraryDayResponseBuilder } from 'gql/autogen/ItineraryDayResponseBuilder';
import { ItineraryItemResponseBuilder } from 'gql/autogen/ItineraryItemResponseBuilder';
import { ItineraryRouteResponseBuilder } from 'gql/autogen/ItineraryRouteResponseBuilder';
import { AddressResponseBuilder } from 'gql/autogen/AddressResponseBuilder';
import { LocationResponseBuilder } from 'gql/autogen/LocationResponseBuilder';
import { GenericContentSectionResponseBuilder } from 'gql/autogen/GenericContentSectionResponseBuilder';
import { GenericContentResponseBuilder } from 'gql/autogen/GenericContentResponseBuilder';
import { MediaResponseBuilder } from 'gql/autogen/MediaResponseBuilder';
import { ReviewSummaryResponseBuilder } from 'gql/autogen/ReviewSummaryResponseBuilder';
import { OfferDetailResponseBuilder } from 'gql/autogen/OfferDetailResponseBuilder';
import { LogisticResponseBuilder } from 'gql/autogen/LogisticResponseBuilder';
import { LogisticLocationInfoResponseBuilder } from 'gql/autogen/LogisticLocationInfoResponseBuilder';
import { buildPricingDisplayResponseBuilder } from 'gql/activityBuilder/pricingDisplayResponseQueryBuilder';
import { buildActivitySummaryResponseQuery } from 'gql/activityBuilder/activitySummaryResponseQueryBuilder';
import { RouteItemResponseBuilder } from 'gql/autogen/RouteItemResponseBuilder';
import {
    buildBenefitItemsReferenceResponse,
    buildDescriptionResponse,
    buildDurationInfoResponse,
    buildLocationRefResponse,
    buildMediaResponse,
    buildSearchErrorResponse,
} from 'gql/activityBuilder/commonQueryBuilders';
import { LocationRefResponseBuilder } from 'gql/autogen/LocationRefResponseBuilder';
import { TicketingResponseBuilder } from 'gql/autogen/TicketingResponseBuilder';
import { RepresentativeInfoResponseBuilder } from 'gql/autogen/RepresentativeInfoResponseBuilder';
import { SupportedLanguagesResponseBuilder } from 'gql/autogen/SupportedLanguagesResponseBuilder';
import { FieldContentTypeResponseBuilder } from 'gql/autogen/FieldContentTypeResponseBuilder';
import { OfferGroupDetailResponseBuilder } from 'gql/autogen/OfferGroupDetailResponseBuilder';
import { BadgesResponseBuilder } from 'gql/autogen/BadgesResponseBuilder';
import { BadgeResponseBuilder } from 'gql/autogen/BadgeResponseBuilder';
import { HighlightResponseBuilder } from 'gql/autogen/HighlightResponseBuilder';
import { RedemptionResponseBuilder } from 'gql/autogen/RedemptionResponseBuilder';
import { OfferTagGroupResponseBuilder } from 'gql/autogen/OfferTagGroupResponseBuilder';
import { OfferTagResponseBuilder } from 'gql/autogen/OfferTagResponseBuilder';

const build = (): DetailsResponseResponseBuilder => {
    // GQL Server throws error when we use fragments at the moment, needs detailed analysis
    // const priceResponse = new PriceResponseBuilder().withInc().withExc();
    // const chargeAmount = new ChargeAmountResponseBuilder().withChargeTotal();
    // const summaryElementResponse = new SummaryElementResponseBuilder().withTotal(new DisplayTotalPriceResponseBuilder().withExclusive(chargeAmount).withAllInclusive(chargeAmount));

    return new DetailsResponseResponseBuilder()
        .withIsSuccess()
        .withResult(
            new DetailsActivityResultResponseBuilder()
                .withIsCompleted()
                .withActivity(
                    new ActivityWithContentResponseBuilder()
                        .withMasterActivityId()
                        .withMasterSupplierId()
                        .withActivityRepresentativeInfo(
                            new RepresentativeInfoResponseBuilder()
                                .withActivityId()
                                .withActivityToken()
                                .withPricingSummary(
                                    new PricingSummaryResponseBuilder().withPricing(new PricingResponseBuilder().withCurrency().withDisplay(buildPricingDisplayResponseBuilder())),
                                )
                                .withSupplierActivityCode()
                                .withCancellationPolicy(
                                    new CancellationResponseBuilder()
                                        .withCancellationType()
                                        .withPolicies(new CancellationPolicyResponseBuilder().withHoursFrom().withHoursUntil().withPenaltyCode()),
                                )
                                .withConfirmMinutes(),
                        )
                        .withContent(
                            new ContentResponseBuilder()
                                .withActivity(buildActivitySummaryResponseQuery())
                                .withDetail(
                                    new DetailResponseBuilder()
                                        .withGenericSection(
                                            new GenericContentSectionResponseBuilder()
                                                .withTitle()
                                                .withSectionType()
                                                .withContent(
                                                    new GenericContentResponseBuilder()
                                                        .withTitle()
                                                        .withDescription()
                                                        .withMedia(new MediaResponseBuilder().withDescription().withSource().withMediaType()),
                                                ),
                                        )
                                        .withLocations(new LocationResponseBuilder().withId().withName().withAddress(new AddressResponseBuilder().withAddressLine()))
                                        .withInclusions(buildBenefitItemsReferenceResponse())
                                        .withExclusions(buildBenefitItemsReferenceResponse())
                                        .withAdditionalDetails(new AdditionalDetailResponseBuilder().withAdditionalType().withDescription())
                                        .withItineraries(
                                            new ItineraryReferenceResponseBuilder()
                                                .withDescription(buildDescriptionResponse())
                                                .withId()
                                                .withItineraryType()
                                                .withItineraryRoutes(
                                                    new ItineraryRouteResponseBuilder()
                                                        .withRoutes(
                                                            new RouteItemResponseBuilder()
                                                                .withDuration(buildDurationInfoResponse())
                                                                .withDescription(buildDescriptionResponse())
                                                                .withStops(new LocationRefResponseBuilder().withLocationRefId().withDescription())
                                                                .withPointsOfInterest(new LocationRefResponseBuilder().withLocationRefId().withDescription()),
                                                        )
                                                        .withDescription(buildDescriptionResponse()),
                                                )
                                                .withItineraryDays(
                                                    new ItineraryDayResponseBuilder()
                                                        .withDescription(buildDescriptionResponse())
                                                        .withDayNumber()
                                                        .withItineraries(
                                                            new ItineraryItemResponseBuilder()
                                                                .withDuration(buildDurationInfoResponse())
                                                                .withDescription(buildDescriptionResponse())
                                                                .withAdmissionIncluded()
                                                                .withPointsOfInterest(buildLocationRefResponse())
                                                                .withMedia(buildMediaResponse()),
                                                        ),
                                                )
                                                .withDuration(buildDurationInfoResponse()),
                                        )
                                        .withOfferDetails(
                                            new OfferDetailResponseBuilder().withTitle().withDescription().withOfferDetailRef().withInclusionRefId().withExclusionRefId(),
                                        )
                                        .withOfferGroupDetails(new OfferGroupDetailResponseBuilder().withOfferGroupDetailsReference().withOfferGroupTitle())
                                        .withLogistics(
                                            new LogisticResponseBuilder()
                                                .withLogisticType()
                                                .withStartEnd(
                                                    new LogisticLocationInfoResponseBuilder()
                                                        .withEndLocation(buildLocationRefResponse())
                                                        .withStartLocation(buildLocationRefResponse()),
                                                ),
                                        )
                                        .withAdditionalDetails(new AdditionalDetailResponseBuilder().withAdditionalType().withDescription().withDescription())
                                        .withTicketing(new TicketingResponseBuilder().withTicketTypes().withRedemption(new RedemptionResponseBuilder().withSpecialInstructions()))
                                        .withAboutActivity()
                                        .withOfferTagGroup(
                                            new OfferTagGroupResponseBuilder().withUseCase().withOfferTags(new OfferTagResponseBuilder().withTagName().withTagValue()),
                                        ),
                                )
                                .withImages(
                                    new ImageResponseBuilder().withDescription().withImageSize(new ImageSizeResponseBuilder().withWidth().withHeight()).withImageType().withUrl(),
                                )
                                .withReviewSummary(new ReviewSummaryResponseBuilder().withAverageScore().withTotalCount())
                                .withContentLocale()
                                .withSupportedLanguages(new SupportedLanguagesResponseBuilder().withLocale())
                                .withBadges(
                                    new BadgesResponseBuilder()
                                        .withActivityHighlights(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withDeals(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withBookingInfo(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withKeyAttributes(new BadgeResponseBuilder().withBadgeType().withEnrichmentData()),
                                )
                                .withActivityHighlights(new HighlightResponseBuilder().withCode()),
                        ),
                )
                .withFieldContentTypes(new FieldContentTypeResponseBuilder().withFieldType().withContentType()),
        )
        .withErrors(buildSearchErrorResponse());
};

export default build;
